import { createTheme } from '@mui/material/styles';
import tokens, { generateColor, generateOpacityColor } from '@verifime/design-tokens';
import { commonComponentsOverride, paletteGrey, typographyTheme } from './common';

const errorContent = generateColor(tokens.brandColorError, '100');
const errorFill = generateOpacityColor(generateColor(tokens.brandColorError, '500'), 0.3);
const warningContent = generateColor(tokens.brandColorWarning, '100');
const warningFill = generateOpacityColor(generateColor(tokens.brandColorWarning, '500'), 0.3);
const infoContent = generateColor(tokens.brandColorInfo, '100');
const infoFill = generateOpacityColor(generateColor(tokens.brandColorInfo, '500'), 0.2);
const successContent = generateColor(tokens.brandColorSuccess, '100');
const successFill = generateOpacityColor(generateColor(tokens.brandColorSuccess, '500'), 0.2);

const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: generateColor(tokens.brandColorPrimary, '400'),
      dark: generateColor(tokens.brandColorPrimary, '300'),
      light: generateColor(tokens.brandColorPrimary, '600'),
      contrastText: tokens.colorWhite,
    },
    secondary: {
      main: generateColor(tokens.brandColorSecondary, '400'),
      dark: generateColor(tokens.brandColorSecondary, '300'),
      light: generateColor(tokens.brandColorSecondary, '600'),
      contrastText: tokens.colorWhite,
    },
    error: {
      main: generateColor(tokens.brandColorError, '500'),
      dark: generateColor(tokens.brandColorError, '400'),
      light: generateColor(tokens.brandColorError, '600'),
      contrastText: tokens.colorWhite,
    },
    warning: {
      main: generateColor(tokens.brandColorWarning, '500'),
      dark: generateColor(tokens.brandColorWarning, '400'),
      light: generateColor(tokens.brandColorWarning, '600'),
      contrastText: generateOpacityColor(tokens.colorBlack, 0.5),
    },
    info: {
      main: generateColor(tokens.brandColorInfo, '500'),
      dark: generateColor(tokens.brandColorInfo, '400'),
      light: generateColor(tokens.brandColorInfo, '600'),
      contrastText: tokens.colorWhite,
    },
    success: {
      main: generateColor(tokens.brandColorSuccess, '500'),
      dark: generateColor(tokens.brandColorSuccess, '400'),
      light: generateColor(tokens.brandColorSuccess, '600'),
      contrastText: tokens.colorWhite,
    },
    text: {
      primary: generateOpacityColor(tokens.colorWhite, 0.9),
      secondary: generateOpacityColor(tokens.colorWhite, 0.6),
      disabled: generateOpacityColor(tokens.colorWhite, 0.4),
    },
    background: {
      default: tokens.colorGrey900,
      paper: tokens.colorGrey800,
    },
    action: {
      disabled: generateOpacityColor(tokens.colorWhite, 0.4),
      disabledBackground: generateOpacityColor(tokens.colorWhite, 0.1),
      disabledOpacity: 0.4,
      focus: generateOpacityColor(tokens.colorWhite, 0.1),
      focusOpacity: 0.1,
      selected: generateColor(tokens.brandColorPrimary, '400'),
      selectedOpacity: 0.4,
      hover: generateOpacityColor(tokens.colorWhite, 0.05),
      hoverOpacity: 0.05,
      active: generateOpacityColor(tokens.colorWhite, 0.5),
      activatedOpacity: 0.1,
    },
    grey: paletteGrey,
  },
  typography: typographyTheme,
  components: {
    MuiButton: {
      variants: [
        ...commonComponentsOverride.MuiButton!.variants!,
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            color: generateColor(tokens.brandColorPrimary, '300'),
          },
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            color: generateColor(tokens.brandColorPrimary, '300'),
          },
        },
        {
          props: { color: 'error' },
          style: {
            color: generateColor(tokens.brandColorError, '300'),
          },
        },
      ],
    },
    MuiLink: {
      defaultProps: {
        color: generateColor(tokens.brandColorPrimary, '300'),
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardError: {
          backgroundColor: errorFill,
          '& .MuiAlertTitle-root': {
            color: errorContent,
          },
          '& .MuiTypography-root': {
            color: errorContent,
          },
          '& button': {
            color: errorContent,
          },
        },
        standardWarning: {
          backgroundColor: warningFill,
          '& .MuiAlertTitle-root': {
            color: warningContent,
          },
          '& .MuiTypography-root': {
            color: warningContent,
          },
          '& button': {
            color: warningContent,
          },
        },
        standardInfo: {
          backgroundColor: infoFill,
          '& .MuiAlertTitle-root': {
            color: infoContent,
          },
          '& .MuiTypography-root': {
            color: infoContent,
          },
          '& button': {
            color: infoContent,
          },
        },
        standardSuccess: {
          backgroundColor: successFill,
          '& .MuiAlertTitle-root': {
            color: successContent,
          },
          '& .MuiTypography-root': {
            color: successContent,
          },
          '& button': {
            color: successContent,
          },
        },
      },
    },
  },
});

export default theme;
